/**
 * React Query hooks for Activity Log APIs
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { activityLogsApi } from '../services/activityLogs';

// =============================================================================
// Query Keys
// =============================================================================

export const activityLogKeys = {
  all: ['activity-logs'] as const,
  project: (projectId: string) => [...activityLogKeys.all, 'project', projectId] as const,
  issue: (projectId: string) => [...activityLogKeys.all, 'issue', projectId] as const,
  testCase: (projectId: string) => [...activityLogKeys.all, 'test-case', projectId] as const,
  mapping: (projectId: string) => [...activityLogKeys.all, 'mapping', projectId] as const,
};

// =============================================================================
// Queries
// =============================================================================

export function useProjectLogs(projectId: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: activityLogKeys.project(projectId),
    queryFn: () => activityLogsApi.getProjectLogs(projectId),
    enabled: !!projectId,
    staleTime: 30000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useIssueLogs(projectId: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: activityLogKeys.issue(projectId),
    queryFn: () => activityLogsApi.getIssueLogs(projectId),
    enabled: !!projectId,
    staleTime: 30000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useTestCaseLogs(projectId: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: activityLogKeys.testCase(projectId),
    queryFn: () => activityLogsApi.getTestCaseLogs(projectId),
    enabled: !!projectId,
    staleTime: 30000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useMappingHistory(projectId: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: activityLogKeys.mapping(projectId),
    queryFn: () => activityLogsApi.getMappingHistory(projectId),
    enabled: !!projectId,
    staleTime: 30000,
    refetchInterval: options?.refetchInterval,
  });
}

// =============================================================================
// Mutations
// =============================================================================

export function useUpdateMapping(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: activityLogsApi.updateMapping,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityLogKeys.mapping(projectId) });
    },
  });
}

export function useDeleteMapping(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subdomain, area }: { subdomain: string; area: string }) =>
      activityLogsApi.deleteMapping(projectId, subdomain, area),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityLogKeys.mapping(projectId) });
    },
  });
}
